import { ProductFilter } from 'containers/RetailMomo/Products/ProductFilter';
import { useCallback, useEffect, useState } from 'react';
import { useOrderStore } from 'store/orderStore';
import { SelectOptions } from 'components/commonType';
import _ from 'lodash';
import { useCoreContext } from 'contexts/coreContext';
import { useCallAPI } from 'hooks/useCallAPI';
import { RdpManager } from 'core/rdp/RdpManager';
import { Retail } from 'core/product/Product';

interface useRetilerManagementProps {
  defaultRdpManager: RdpManager;
}

const useRetailerManagement = (
  initialState: useRetilerManagementProps = {
    defaultRdpManager: {} as RdpManager,
  },
) => {
  const { defaultRdpManager } = initialState;
  const { callAPIs } = useCallAPI();
  const core = useCoreContext();
  const agencyId = _.get(core, 'accountManager.localeMeta.agencyId');

  const [retailerTypeOptions, setRetailerTypeOptions] = useState<
    (SelectOptions & { name: string })[]
  >([]);

  const [filters, setFilters] = useState<ProductFilter>({
    advertiser: useOrderStore.getState().order.advertiserId,
  });
  const advertiser = filters.advertiser;
  const [cache, setCache] = useState<{ [key: string]: any }>({});

  // 取得廠商選項
  const fetchRetailerOptions = useCallback(
    async (advertiser: number) => {
      if (!agencyId) {
        return;
      }
      try {
        await callAPIs(
          [
            defaultRdpManager.getRetailsOfAdvertiser.bind(
              defaultRdpManager,
              agencyId,
              advertiser,
            ),
          ],
          (retailers: Retail[]) => {
            const newRetailerTypeOptions = [
              ...retailers.map((retailer) => ({
                label: retailer.name,
                value: retailer.id,
                name: retailer.name,
              })),
            ];
            setRetailerTypeOptions(newRetailerTypeOptions);
          },
        );
      } catch (error) {
        console.error('Failed to fetch data:', error);
      }
    },

    [agencyId, callAPIs, defaultRdpManager],
  );

  // 設定初始廠商
  const setInitRetailer = useCallback((initRetailer, cache) => {
    const cacheKey = `${initRetailer[0].name}_${initRetailer[0].value}`;
    if (cache[cacheKey]) {
      return;
    }
    setFilters((filter) => {
      const originRetailerId = _.get(filter, 'retailerId', '');
      const retailer = initRetailer.find(
        (retailer) => retailer.value.toString() === originRetailerId,
      );
      return {
        ...filter,
        retailerId: retailer
          ? originRetailerId
          : initRetailer[0].value.toString(),
      };
    });
    setCache({
      [cacheKey]: initRetailer,
    });
  }, []);

  useEffect(() => {
    if (!_.isNil(advertiser)) {
      fetchRetailerOptions(advertiser);
    }
  }, [advertiser, fetchRetailerOptions]);

  useEffect(() => {
    if (!_.isEmpty(retailerTypeOptions)) {
      setInitRetailer(retailerTypeOptions, cache);
    }
  }, [setInitRetailer, retailerTypeOptions, cache]);

  return { filters, retailerTypeOptions };
};

export default useRetailerManagement;
